import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";
import { computeOccupancy, matchRequest } from "./utils";

const initialState: State = [];

function handleRequest(
  state: State,
  request: Boundaries["retrieveOccupancy"]["request"],
): State {
  const { from, to, granularity, filter, sort, size } = request;
  const index = matchRequest(state, from, to, granularity, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          from,
          to,
          granularity,
          filter,
          sort,
          size,
          request: setRequestStateToPending(),
          content: [],
          currentPage: 0,
          canLoadMore: true,
          totalElements: 0,
        },
      ];
}

function handleSuccess(
  state: State,
  {
    request,
    content,
    page,
    size,
    totalElements,
  }: Boundaries["retrieveOccupancy"]["success"],
): State {
  const { from, to, granularity, filter, sort } = request;
  const index = matchRequest(state, from, to, granularity, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          request: setRequestStateToSuccess(),
          content:
            page === 0
              ? content
              : [
                  ...state[index].content.slice(0, page * size),
                  ...content,
                  ...state[index].content.slice(page * size + size),
                ],
          currentPage: page + 1,
          canLoadMore: (page + 1) * size < totalElements,
          totalElements,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          from,
          to,
          granularity,
          filter,
          sort,
          size,
          request: setRequestStateToSuccess(),
          content,
          currentPage: page + 1,
          canLoadMore: (page + 1) * size < totalElements,
          totalElements,
        },
      ];
}

function handleAdjustWorkloadSuccess(
  state: State,
  { request, period }: Boundaries["adjustWorkload"]["success"],
): State {
  return state.map((entry) =>
    entry.granularity !== request.granularity
      ? entry
      : {
          ...entry,
          content: entry.content.map((resource) =>
            resource.uuid === request.resourceId
              ? {
                  ...resource,
                  periods: resource.periods.map((resourcePeriod) =>
                    resourcePeriod.from >= period.from &&
                    resourcePeriod.from <= period.to
                      ? {
                          ...resourcePeriod,
                          ...computeOccupancy(
                            period.consolidated.load,
                            period.consolidated.capacity,
                          ),
                        }
                      : resourcePeriod,
                  ),
                }
              : resource,
          ),
        },
  );
}

function handleAdjustCapacitySuccess(
  state: State,
  { request }: Boundaries["adjustCapacity"]["success"],
): State {
  return state.filter((entry) => entry.granularity !== request.granularity);
}

function handleFailure(
  state: State,
  { request }: Boundaries["retrieveOccupancy"]["failure"],
): State {
  const { from, to, granularity, filter, sort, size } = request;
  const index = matchRequest(state, from, to, granularity, filter, sort, size);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          from,
          to,
          granularity,
          filter,
          sort,
          size,
          request: setRequestStateToFailure(),
          content: [],
          currentPage: 0,
          canLoadMore: true,
          totalElements: 0,
        },
      ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveOccupancyRequested]: handleRequest,
    [events.retrieveOccupancySucceeded]: handleSuccess,
    [events.retrieveOccupancyFailed]: handleFailure,
    [events.adjustWorkloadSucceeded]: handleAdjustWorkloadSuccess,
    [events.adjustCapacitySucceeded]: handleAdjustCapacitySuccess,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
